import { Button, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/lib/table';
import { CheckCircleFilled } from '@ant-design/icons';
import type { Moment } from 'moment';

import type { ClientService } from '../../../shared/api/ClientService';
import type { LocaleObj } from '../../../locale/LocalizedStrings';
import EventStatusTag from './EventStatusTag';
import InfoLabel from './InfoLabel';
import { EMPTY_VALUE, EVENT_COLUMN_WIDTH, REPLAYABLE_ICON_COLOR } from './constants';
import type { EventSortColumnKey, EventStatus, IEventSort } from './types';
import { formatDateTime } from './utils';

type IntegrationEvent = ClientService.IntegrationEventDto;

interface IOptions {
  t: LocaleObj;
  sort: IEventSort;
  onView: (event: IntegrationEvent) => void;
}

export const getEventColumns = ({ t, sort, onView }: IOptions): ColumnsType<IntegrationEvent> => {
  const sortOrderOf = (columnKey: EventSortColumnKey) => (sort.columnKey === columnKey ? sort.order : null);

  return [
    {
      title: t.INTEGRATION_EVENT_ID,
      dataIndex: 'id',
      key: 'id',
      width: EVENT_COLUMN_WIDTH.eventId,
      render: (id?: string) =>
        id ? (
          <Typography.Text copyable ellipsis={{ tooltip: id }}>
            {id}
          </Typography.Text>
        ) : (
          EMPTY_VALUE
        ),
    },
    {
      title: t.INTEGRATION_EVENT_TYPE,
      dataIndex: 'eventType',
      key: 'eventType',
      width: EVENT_COLUMN_WIDTH.eventType,
      sorter: true,
      sortOrder: sortOrderOf('eventType'),
      render: (eventType?: string) => eventType || EMPTY_VALUE,
    },
    {
      title: t.INTEGRATION_OCCURRED,
      dataIndex: 'occurredTime',
      key: 'occurredTime',
      width: EVENT_COLUMN_WIDTH.occurred,
      sorter: true,
      sortOrder: sortOrderOf('occurredTime'),
      // Clearing the sort returns to this column descending, so it must come first in the cycle.
      sortDirections: ['descend', 'ascend'],
      render: (occurredTime?: Moment) => formatDateTime(occurredTime),
    },
    {
      title: t.INTEGRATION_RECEIVED,
      dataIndex: 'receivedTime',
      key: 'receivedTime',
      width: EVENT_COLUMN_WIDTH.received,
      sorter: true,
      sortOrder: sortOrderOf('receivedTime'),
      render: (receivedTime?: Moment) => formatDateTime(receivedTime),
    },
    {
      title: t.STATUS,
      dataIndex: 'status',
      key: 'status',
      width: EVENT_COLUMN_WIDTH.status,
      sorter: true,
      sortOrder: sortOrderOf('status'),
      render: (status?: EventStatus) => <EventStatusTag status={status} />,
    },
    {
      title: t.INTEGRATION_FILE_NAME,
      dataIndex: 'fileName',
      key: 'fileName',
      width: EVENT_COLUMN_WIDTH.fileName,
      render: (fileName?: string) => fileName || EMPTY_VALUE,
    },
    {
      title: <InfoLabel label={t.INTEGRATION_CAN_REAPPLY} hint={t.INTEGRATION_CAN_REAPPLY_HINT} />,
      dataIndex: 'canBeReapplied',
      key: 'canBeReapplied',
      width: EVENT_COLUMN_WIDTH.canReapply,
      render: (canBeReapplied?: boolean) =>
        canBeReapplied ? (
          <Tooltip title={t.INTEGRATION_CAN_REAPPLY_YES}>
            <CheckCircleFilled aria-label={t.INTEGRATION_CAN_REAPPLY_YES} style={{ color: REPLAYABLE_ICON_COLOR }} />
          </Tooltip>
        ) : (
          EMPTY_VALUE
        ),
    },
    {
      title: t.ACTIONS,
      key: 'actions',
      width: EVENT_COLUMN_WIDTH.actions,
      fixed: 'right',
      render: (_: unknown, event: IntegrationEvent) => (
        <Button type="link" size="small" onClick={() => onView(event)}>
          {t.VIEW}
        </Button>
      ),
    },
  ];
};
